import eStorePage = overwolf.utils.enums.eStorePage;
import Display = overwolf.utils.Display;
import Result = overwolf.Result;

type Manifest = overwolf.extensions.Manifest;

// -----------------------------------------------------------------------------
export class OWUtils {
  // ---------------------------------------------------------------------------
  public static getViewportSize(): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      overwolf.utils.getMonitorsList((result) => {
        let width = -1,
          height = -1;

        for (const display of result.displays) {
          if (display.is_primary) {
            width = display.width;
            height = display.height;
          }
        }

        overwolf.games.getRunningGameInfo(async (game) => {
          if (game && game.isInFocus) {
            width = game.width;
            height = game.height;
          }

          if (width !== null && height !== null) {
            resolve({ width, height });
          } else {
            reject(new Error('could not get monitors width/height'));
          }
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static getDPIScale(): Promise<number> {
    return new Promise((resolve) => {
      overwolf.windows.getWindowDPI((result) => {
        return resolve(result.scale);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static getAppVersion(): Promise<string> {
    return new Promise((resolve) => {
      overwolf.extensions.current.getManifest((manifest) => {
        return resolve(manifest.meta.version);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static getSystemInformation(): Promise<overwolf.utils.SystemInfo> {
    return new Promise((resolve) => {
      overwolf.utils.getSystemInformation((result) => {
        if (!result.success) {
          return resolve({});
        }

        return resolve(result.systemInfo);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static placeOnClipboard(data): void {
    overwolf.utils.placeOnClipboard(data);
  }

  // ---------------------------------------------------------------------------
  public static openUrlInDefaultBrowser(url: string): void {
    overwolf.utils.openUrlInDefaultBrowser(url);
  }

  // ---------------------------------------------------------------------------
  public static openStore(uid: string, page: eStorePage): void {
    overwolf.utils.openStore({
      uid,
      page: page,
    });
  }

  // ---------------------------------------------------------------------------
  public static openFolderPicker(
    initialPath: string,
  ): Promise<overwolf.utils.OpenFolderPickerResult> {
    return new Promise((resolve) => {
      overwolf.utils.openFolderPicker(initialPath, (res) => {
        resolve(res);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static openFilePicker(
    filter?: string,
    initialPath?: string,
    multiSelect?: boolean,
  ): Promise<overwolf.utils.OpenFilePickerResult> {
    return new Promise((resolve) => {
      overwolf.utils.openFilePicker(
        filter,
        initialPath,
        (res) => {
          resolve(res);
        },
        multiSelect,
      );
    });
  }

  // ---------------------------------------------------------------------------
  public static async getMonitorsList(): Promise<Display[]> {
    return new Promise((resolve) => {
      overwolf.utils.getMonitorsList((result) => {
        resolve(result.displays);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static async openWindowsExplorer(filepath: string): Promise<Result> {
    return new Promise((resolve) => {
      overwolf.utils.openWindowsExplorer(filepath, resolve);
    });
  }

  // ---------------------------------------------------------------------------
  public static async encryptForUser(plaintext: string): Promise<string> {
    return new Promise((resolve) => {
      overwolf.cryptography.encryptForCurrentUser(plaintext, (result) => {
        resolve(result.ciphertext);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static async decryptForUser(ciphertext: string): Promise<string> {
    return new Promise((resolve) => {
      overwolf.cryptography.decryptForCurrentUser(ciphertext, (result) => {
        resolve(result.plaintext);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static async openStoreOneAppPage(uid: string) {
    overwolf.utils.openStoreOneAppPage(uid);
  }

  // ---------------------------------------------------------------------------
  // Check if |getClientInfo| is supported on the current Overwolf client ver.
  public static isGetClientInfoSupported(): boolean {
    return typeof overwolf.utils.getClientInfo === 'function';
  }

  // ---------------------------------------------------------------------------
  public static getClientInfo(): Promise<overwolf.utils.ClientInfoResult> {
    return new Promise((resolve) => {
      overwolf.utils.getClientInfo((result) => {
        if (!result?.success) {
          return resolve(null);
        }

        return resolve(result);
      });
    });
  }
}
